import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, X, Flag, Star, Trash2, Save } from "lucide-react";
import { StarDisplay } from "@/components/reviews/Stars";
import { adminFetchReviews, adminPatchReview } from "@/lib/api";
import { adminInputCls, adminBtnCls, adminTabCls } from "./shared";

const TABS = ["pending", "approved", "rejected", "spam"] as const;

interface AdminReview {
  id: string;
  name: string;
  company: string;
  project_type: string;
  service_used: string;
  brand: string;
  rating: number;
  review: string;
  city: string;
  other_text: string;
  contact: string;
  status: string;
  featured: boolean;
  display_company: boolean;
  display_name: string;
  edited_text: string;
  created_at: string;
}

export const ReviewsTab = ({ onAuthError }: { onAuthError: () => void }) => {
  const [tab, setTab] = useState<(typeof TABS)[number]>("pending");
  const [reviews, setReviews] = useState<AdminReview[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { edited_text: string; display_name: string }>>({});

  const load = useCallback(() => {
    setReviews(null);
    adminFetchReviews(tab)
      .then((r) => setReviews(r.data.reviews))
      .catch((e) => {
        if (e?.response?.status === 401) onAuthError();
        else {
          toast.error("Failed to load reviews");
          setReviews([]);
        }
      });
  }, [tab, onAuthError]);

  useEffect(() => load(), [load]);

  const patch = async (id: string, payload: Record<string, unknown>) => {
    try {
      await adminPatchReview(id, payload);
      load();
    } catch (err: any) {
      if (err?.response?.status === 401) onAuthError();
      else toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  const draftFor = (r: AdminReview) =>
    drafts[r.id] || { edited_text: r.edited_text || "", display_name: r.display_name || "" };

  return (
    <div data-testid="admin-reviews-tab">
      <div
        data-testid="admin-policy-note"
        className="border border-gold/20 bg-gold/5 p-5 text-xs leading-relaxed text-neutral-400 md:text-sm"
      >
        <span className="font-bold uppercase tracking-[0.15em] text-gold">Moderation policy: </span>
        Approve legitimate reviews — positive or negative. Use Reject/Spam/Delete only for spam,
        fake submissions, abusive or inappropriate content, duplicates, or reviews exposing
        private information. Star ratings and the substance of a review can never be edited.
      </div>

      <div className="mt-6 flex flex-wrap gap-2.5">
        {TABS.map((s) => (
          <button key={s} data-testid={`admin-tab-${s}`} onClick={() => setTab(s)} className={adminTabCls(tab === s)}>
            {s}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-6" data-testid="admin-review-list">
        {reviews === null ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
          </div>
        ) : reviews.length === 0 ? (
          <p data-testid="admin-empty" className="border border-dashed border-white/15 p-10 text-center text-sm text-neutral-500">
            No {tab} reviews.
          </p>
        ) : (
          reviews.map((r) => (
            <div key={r.id} data-testid={`admin-review-card-${r.id}`} className="border border-white/10 bg-charcoal p-6 md:p-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-black tracking-tight text-white">
                    {r.name}
                    {r.company && <span className="ml-2 text-sm font-bold text-neutral-400">· {r.company}</span>}
                  </p>
                  <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                    {r.brand === "r3" ? "R3 Services" : "R3stan Drywall"} · {r.service_used || "—"} · {r.project_type || "—"}
                    {r.city && ` · ${r.city}`}
                    {r.other_text && (
                      <span className="block normal-case tracking-normal text-neutral-400">
                        Other: {r.other_text}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <StarDisplay rating={r.rating} />
                  {r.featured && (
                    <span className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.15em] text-gold">
                      Featured
                    </span>
                  )}
                </div>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-neutral-300">“{r.review}”</p>
              {r.edited_text && (
                <p className="mt-3 border-l-2 border-gold/50 pl-3 text-sm leading-relaxed text-neutral-400">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">Edited display text: </span>
                  {r.edited_text}
                </p>
              )}
              <p className="mt-3 text-[11px] uppercase tracking-[0.15em] text-neutral-600">
                {new Date(r.created_at).toLocaleString()}
                {r.contact && <span className="ml-3 text-neutral-500">Private contact: {r.contact}</span>}
              </p>

              <div className="mt-5 flex flex-wrap gap-2.5">
                {r.status !== "approved" && (
                  <button data-testid={`admin-approve-${r.id}`} onClick={() => patch(r.id, { action: "approve" })} className={adminBtnCls}>
                    <Check className="h-3.5 w-3.5" /> Approve
                  </button>
                )}
                {r.status === "approved" && !r.featured && (
                  <button data-testid={`admin-feature-${r.id}`} onClick={() => patch(r.id, { action: "feature" })} className={adminBtnCls}>
                    <Star className="h-3.5 w-3.5" /> Feature
                  </button>
                )}
                {r.status === "approved" && r.featured && (
                  <button data-testid={`admin-unfeature-${r.id}`} onClick={() => patch(r.id, { action: "unfeature" })} className={adminBtnCls}>
                    <Star className="h-3.5 w-3.5" /> Unfeature
                  </button>
                )}
                {r.status !== "rejected" && (
                  <button data-testid={`admin-reject-${r.id}`} onClick={() => patch(r.id, { action: "reject" })} className={adminBtnCls}>
                    <X className="h-3.5 w-3.5" /> Reject
                  </button>
                )}
                {r.status !== "spam" && (
                  <button data-testid={`admin-spam-${r.id}`} onClick={() => patch(r.id, { action: "spam" })} className={adminBtnCls}>
                    <Flag className="h-3.5 w-3.5" /> Spam
                  </button>
                )}
                <button
                  data-testid={`admin-delete-${r.id}`}
                  onClick={() => {
                    if (window.confirm("Delete this review permanently?")) patch(r.id, { action: "delete" });
                  }}
                  className={`${adminBtnCls} hover:border-red-500/60 hover:text-red-400`}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>

              <div className="mt-6 grid gap-4 border-t border-white/10 pt-5 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
                    Display name override (optional)
                  </label>
                  <input
                    data-testid={`admin-displayname-${r.id}`}
                    value={draftFor(r).display_name}
                    onChange={(e) =>
                      setDrafts((d) => ({ ...d, [r.id]: { ...draftFor(r), display_name: e.target.value } }))
                    }
                    placeholder="First name + last initial is the default"
                    className={adminInputCls}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
                    Minor formatting / remove personal info (never change substance)
                  </label>
                  <input
                    data-testid={`admin-editedtext-${r.id}`}
                    value={draftFor(r).edited_text}
                    onChange={(e) =>
                      setDrafts((d) => ({ ...d, [r.id]: { ...draftFor(r), edited_text: e.target.value } }))
                    }
                    placeholder="Leave empty to show the original review"
                    className={adminInputCls}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-4 md:col-span-2">
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-neutral-400">
                    <input
                      data-testid={`admin-displaycompany-${r.id}`}
                      type="checkbox"
                      checked={r.display_company}
                      onChange={(e) => patch(r.id, { display_company: e.target.checked })}
                      className="h-4 w-4"
                    />
                    Show company name publicly
                  </label>
                  <button data-testid={`admin-save-${r.id}`} onClick={() => patch(r.id, draftFor(r))} className={adminBtnCls}>
                    <Save className="h-3.5 w-3.5" /> Save display options
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
